import React, { useEffect, useMemo } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import Navbar from "../../layout/Navbar"
import Review from "../../layout/Review"
import DownloadSection from "../../layout/downloadSection"
import Footer from "../../layout/Footer"
import UpdateCabForm from "./UpdateCabForm"
import MainCab from "./MainCab"
import CustomerFAQ from "./CustomerFAQ"
import SEO from "../../components/SEO"
import ProgressStepper from "../../components/ProgressStepper"
import CityPairPage from "../CityPair"
import { slugify } from "../../utils/slugify"

const Cab = () => {
  const { location } = useSelector((state) => state.location)
  const { search } = useLocation() // gets the search part of the URL
  const navigate = useNavigate()

  useEffect(() => {
    console.log("search", search)

    if (!search) {
      navigate("/")
      return
    }
    // Scroll to top when the pathname changes
    window.scrollTo(0, 0)
  }, [])

  // Memoize the queryParams object
  const queryParams = useMemo(() => new URLSearchParams(search), [search])

  const tripData = localStorage.getItem("tripData")

  // Memoize the tripDetails object
  const tripDetails = useMemo(() => {
    const storedData = tripData ? JSON.parse(tripData) : {}
    
    // Helper to parse number from query param
    const parseNumber = (value) => {
      if (!value || value === "") return ""
      const num = Number(parseFloat(value))
      return isNaN(num) ? "" : num
    }
    
    // Get values from query params first, then fallback to stored data
    const parsed = {
      ...storedData,
      trip_type: parseInt(queryParams.get("trip_type")) || storedData.trip_type || "",
      round_trip: parseInt(queryParams.get("round_trip")) || storedData.round_trip || 0,
      pickup: queryParams.get("pickup") || storedData.pickup || "",
      drop: queryParams.get("drop") || storedData.drop || "",
      pickup_lat: parseNumber(queryParams.get("pickup_lat")) || storedData.pickup_lat || "",
      pickup_long: parseNumber(queryParams.get("pickup_long")) || storedData.pickup_long || "",
      drop_lat: parseNumber(queryParams.get("drop_lat")) || storedData.drop_lat || "",
      drop_long: parseNumber(queryParams.get("drop_long")) || storedData.drop_long || "",
      pickup_city: queryParams.get("pickup_city") || storedData.pickup_city || "",
      drop_city: queryParams.get("drop_city") || storedData.drop_city || "",
      trip_date: queryParams.get("trip_date") || storedData.trip_date || "",
      trip_time: queryParams.get("trip_time") || storedData.trip_time || "",
      phone_number: queryParams.get("phone_number") || storedData.phone_number || "",
      return_date: queryParams.get("return_date") || storedData.return_date || "",
      return_time: queryParams.get("return_time") || storedData.return_time || "",
      package_type_in_hours: queryParams.get("package_type_in_hours") || storedData.package_type_in_hours || "",
      ip_address: location?.ip || storedData.ip_address || 8425,
    }
    
    return parsed
  }, [queryParams, location, tripData])

  useEffect(() => {
    localStorage.setItem("tripData", JSON.stringify(tripDetails))
  }, [tripDetails])

  // Check if this is an outstation one-way trip with city names
  const shouldShowCityPairPage = useMemo(() => {
    const tripType = parseInt(queryParams.get("trip_type"))
    const roundTrip = parseInt(queryParams.get("round_trip"))
    const pickupCity = queryParams.get("pickup_city")
    const dropCity = queryParams.get("drop_city")

    // Show CityPair page if: trip_type=3 (outstation), round_trip=0 or 1, and both cities are present
    return tripType === 3 && (roundTrip === 0 || roundTrip === 1) && pickupCity && dropCity
  }, [queryParams])

  // Extract city slugs for CityPair page
  const citySlugs = useMemo(() => {
    if (!shouldShowCityPairPage) return null

    const pickupCity = queryParams.get("pickup_city")
    const dropCity = queryParams.get("drop_city")

    return {
      fromCity: slugify(pickupCity),
      toCity: slugify(dropCity),
    }
  }, [shouldShowCityPairPage, queryParams])

  // If conditions are met, render CityPairPage
  if (shouldShowCityPairPage && citySlugs) {
    return <CityPairPage fromCity={citySlugs.fromCity} toCity={citySlugs.toCity} />
  }

  return (
    <div>
      <SEO 
        title="Book Cab"
        description="Book your cab with Pulpit - Choose from local, outstation, or hourly rental services. Find the perfect vehicle for your journey with transparent pricing and verified drivers."
        keywords="book cab, taxi booking, cab service, outstation cab, local cab, hourly rental, Pulpit cab booking"
      />
      <Navbar />
      <ProgressStepper currentStep={2} />
      <UpdateCabForm />
      <MainCab
        tripDetails={tripDetails}
      />

      {/* Popular Routes Sections */}
      <div className="w-[86%] mx-auto px-3 sm:px-6 lg:px-8 py-12 space-y-12">
        {/* Top Cab Routes */}
        <div className="bg-white rounded-[10px] shadow-sm px-[12px] py-[12px] md:px-[44px] md:py-[32px] border">
          <h2 className="text-[16px] font-black text-[#1B1B1B] mb-6">
            Top Cab Routes
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-3">
              <a
                href="/delhi-to-jaipur-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi To Jaipur Cab
              </a>
              <a
                href="/delhi-to-dehradun-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi To Dehradun Cab
              </a>
              <a
                href="/delhi-to-haldwani-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi To Haldlwani Cab
              </a>
              <a
                href="/dehradun-to-delhi-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Dehradun to Delhi Cab
              </a>
              <a
                href="/dehradun-to-mumbai-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Dehradun to Mumbai Cab
              </a>
            </div>
            <div className="space-y-3">
              <a
                href="/delhi-to-agra-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi To Agra Cab
              </a>
              <a
                href="/delhi-to-haridwar-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi To Haridwar Cab
              </a>
              <a
                href="/delhi-to-manali-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi To Manali Cab
              </a>
              <a
                href="/bhubaneswar-to-puri-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Bhubaneswar to Puri Cab
              </a>
              <a
                href="/ahmedabad-to-vadodara-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Ahmedabad to Vadodara Cab
              </a>
            </div>
            <div className="space-y-3">
              <a
                href="/mumbai-to-pune-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Mumbai To Pune Cab
              </a>
              <a
                href="/chandigarh-to-shimla-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Chandigarh To Shimla Cab
              </a>
              <a
                href="/bangalore-to-mysore-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Bangalore To Mysore Cab
              </a>
              <a
                href="/siliguri-to-gangtok-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Siliguri to Gangtok Cab
              </a>
              <a
                href="/bangalore-to-hyderabad-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Bangalore to Hyderabad Cab
              </a>
            </div>
            <div className="space-y-3">
              <a
                href="/delhi-to-chandigarh-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi To Chandigarh Cab
              </a>
              <a
                href="/delhi-to-mathura-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi To Mathura Cab
              </a>
              <a
                href="/jaipur-to-delhi-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Jaipur To Delhi Cab
              </a>
              <a
                href="/mysore-to-coorg-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Mysore to Coorg Cab
              </a>
              <a
                href="/delhi-to-bareilly-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi to Bareilly Cab
              </a>
            </div>
          </div>
        </div>

        {/* Cab from Top Cities */}
        <div className="bg-white rounded-[10px] shadow-sm px-[12px] py-[12px] md:px-[44px] md:py-[32px] border">
          <h2 className="text-[16px] font-black text-[#1B1B1B] mb-6">
            Cab from Top Cities
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {/* Same grid structure as above, with identical content */}
            <div className="space-y-3">
              <a
                href="/delhi-to-jaipur-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi To Jaipur Cab
              </a>
              <a
                href="/delhi-to-dehradun-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi To Dehradun Cab
              </a>
              <a
                href="/delhi-to-haldwani-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi To Haldlwani Cab
              </a>
              <a
                href="/dehradun-to-delhi-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Dehradun to Delhi Cab
              </a>
              <a
                href="/dehradun-to-mumbai-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Dehradun to Mumbai Cab
              </a>
            </div>
            <div className="space-y-3">
              <a
                href="/delhi-to-agra-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi To Agra Cab
              </a>
              <a
                href="/delhi-to-haridwar-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi To Haridwar Cab
              </a>
              <a
                href="/delhi-to-manali-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi To Manali Cab
              </a>
              <a
                href="/bhubaneswar-to-puri-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Bhubaneswar to Puri Cab
              </a>
              <a
                href="/ahmedabad-to-vadodara-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Ahmedabad to Vadodara Cab
              </a>
            </div>
            <div className="space-y-3">
              <a
                href="/mumbai-to-pune-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Mumbai To Pune Cab
              </a>
              <a
                href="/chandigarh-to-shimla-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Chandigarh To Shimla Cab
              </a>
              <a
                href="/bangalore-to-mysore-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Bangalore To Mysore Cab
              </a>
              <a
                href="/siliguri-to-gangtok-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Siliguri to Gangtok Cab
              </a>
              <a
                href="/bangalore-to-hyderabad-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Bangalore to Hyderabad Cab
              </a>
            </div>
            <div className="space-y-3">
              <a
                href="/delhi-to-chandigarh-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi To Chandigarh Cab
              </a>
              <a
                href="/delhi-to-mathura-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi To Mathura Cab
              </a>
              <a
                href="/jaipur-to-delhi-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Jaipur To Delhi Cab
              </a>
              <a
                href="/mysore-to-coorg-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Mysore to Coorg Cab
              </a>
              <a
                href="/delhi-to-bareilly-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi to Bareilly Cab
              </a>
            </div>
          </div>
        </div>

        {/* Top Airport Cab Routes */}
        <div className="bg-white rounded-[10px] shadow-sm px-[12px] py-[12px] md:px-[44px] md:py-[32px] border">
          <h2 className="text-[16px] font-black text-[#1B1B1B] mb-6">
            Top Airport Cab Routes
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {/* Same grid structure as above, with identical content */}
            <div className="space-y-3">
              <a
                href="/delhi-to-jaipur-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi To Jaipur Cab
              </a>
              <a
                href="/delhi-to-dehradun-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi To Dehradun Cab
              </a>
              <a
                href="/delhi-to-haldwani-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi To Haldlwani Cab
              </a>
              <a
                href="/dehradun-to-delhi-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Dehradun to Delhi Cab
              </a>
              <a
                href="/dehradun-to-mumbai-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Dehradun to Mumbai Cab
              </a>
            </div>
            <div className="space-y-3">
              <a
                href="/delhi-to-agra-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi To Agra Cab
              </a>
              <a
                href="/delhi-to-haridwar-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi To Haridwar Cab
              </a>
              <a
                href="/delhi-to-manali-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi To Manali Cab
              </a>
              <a
                href="/bhubaneswar-to-puri-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Bhubaneswar to Puri Cab
              </a>
              <a
                href="/ahmedabad-to-vadodara-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Ahmedabad to Vadodara Cab
              </a>
            </div>
            <div className="space-y-3">
              <a
                href="/mumbai-to-pune-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Mumbai To Pune Cab
              </a>
              <a
                href="/chandigarh-to-shimla-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Chandigarh To Shimla Cab
              </a>
              <a
                href="/bangalore-to-mysore-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Bangalore To Mysore Cab
              </a>
              <a
                href="/siliguri-to-gangtok-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Siliguri to Gangtok Cab
              </a>
              <a
                href="/bangalore-to-hyderabad-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Bangalore to Hyderabad Cab
              </a>
            </div>
            <div className="space-y-3">
              <a
                href="/delhi-to-chandigarh-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi To Chandigarh Cab
              </a>
              <a
                href="/delhi-to-mathura-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi To Mathura Cab
              </a>
              <a
                href="/jaipur-to-delhi-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Jaipur To Delhi Cab
              </a>
              <a
                href="/mysore-to-coorg-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Mysore to Coorg Cab
              </a>
              <a
                href="/delhi-to-bareilly-cab"
                className="text-blue-600 hover:text-blue-800 block"
              >
                Delhi to Bareilly Cab
              </a>
            </div>
          </div>
        </div>
      </div>
      <CustomerFAQ> </CustomerFAQ>
      <Review />
      <DownloadSection />
      <Footer />
    </div>
  )
}

export default Cab
